import type { OrderDetailsUI } from "@/domain/order/order-details.model"
import { OrderStatus } from "@/types/enums/OrderStatus"

/**
 * Customer refund details for a cancelled order ("what happened to my money").
 *
 * The server response is authoritative: nothing here infers a refund from order status, payment
 * status, `is_paid` or `is_refunded`. Unknown values fail safe to UNAVAILABLE — never to refunded.
 */
export const RefundDetailsStatus = {
  Requested: "REQUESTED",
  Processing: "PROCESSING",
  PartiallyRefunded: "PARTIALLY_REFUNDED",
  Refunded: "REFUNDED",
  Unsuccessful: "UNSUCCESSFUL",
  Unavailable: "UNAVAILABLE",
} as const

export type RefundDetailsStatusValue = (typeof RefundDetailsStatus)[keyof typeof RefundDetailsStatus]

export type RefundHistoryEntryUI = {
  status: RefundDetailsStatusValue
  amount: number
  requestedAtUtc: string
  completedAtUtc: string | null
}

export type OrderRefundDetailsUI =
  | { kind: "no-record"; message: string }
  | {
      kind: "details"
      status: RefundDetailsStatusValue
      message: string
      currencyCode: string | null
      originalPaymentAmount: number | null
      refundedAmount: number | null
      inProgressAmount: number | null
      history: RefundHistoryEntryUI[]
    }

export const REFUND_DETAILS_FALLBACK_MESSAGE = "Refund status is currently unavailable. Please contact the restaurant."

/** Customer label for each status; the server message carries the explanation. */
export const RefundDetailsStatusLabel: Record<RefundDetailsStatusValue, string> = {
  REQUESTED: "Refund requested",
  PROCESSING: "Refund processing",
  PARTIALLY_REFUNDED: "Partially refunded",
  REFUNDED: "Refunded",
  UNSUCCESSFUL: "Refund unsuccessful",
  UNAVAILABLE: "Status unavailable",
}

const KNOWN_STATUSES = new Set<string>(Object.values(RefundDetailsStatus))

/**
 * The "View refund details" action is offered for cancelled orders that were paid. This is only a
 * UI hint — the endpoint independently decides whether and what refund information exists.
 */
export function canViewRefundDetails(order: Pick<OrderDetailsUI, "orderStatus" | "isPaid">): boolean {
  return order.orderStatus === OrderStatus.Cancelled && order.isPaid === true
}

type JsonObject = Record<string, unknown>

function isRecord(v: unknown): v is JsonObject {
  return v != null && typeof v === "object" && !Array.isArray(v)
}

function text(v: unknown): string {
  return typeof v === "string" ? v.trim() : ""
}

function amountOrNull(v: unknown): number | null {
  if (v == null || v === "") return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function parseStatus(v: unknown): RefundDetailsStatusValue | null {
  const s = text(v).toUpperCase()
  return KNOWN_STATUSES.has(s) ? (s as RefundDetailsStatusValue) : null
}

/**
 * Maps the `data` of `GET …/orders/{id}/refund-details`. An unrecognised status, or a response that
 * is not an object, becomes UNAVAILABLE with no amounts.
 */
export function mapOrderRefundDetails(data: unknown): OrderRefundDetailsUI {
  if (!isRecord(data)) return unavailable()

  if (data.has_refund_record === false) {
    return { kind: "no-record", message: text(data.message) || "Refund information is not currently available." }
  }

  const status = parseStatus(data.status)
  if (status === null || status === RefundDetailsStatus.Unavailable) return unavailable(text(data.message))

  const history: RefundHistoryEntryUI[] = []
  for (const row of Array.isArray(data.history) ? data.history : []) {
    if (!isRecord(row)) continue
    const entryStatus = parseStatus(row.status)
    const amount = amountOrNull(row.amount)
    // An entry we cannot read makes the whole answer unreliable.
    if (entryStatus === null || amount === null) return unavailable()
    history.push({
      status: entryStatus,
      amount,
      requestedAtUtc: text(row.requested_at_utc),
      completedAtUtc: text(row.completed_at_utc) || null,
    })
  }

  return {
    kind: "details",
    status,
    message: text(data.message) || REFUND_DETAILS_FALLBACK_MESSAGE,
    currencyCode: text(data.currency_code) || null,
    originalPaymentAmount: amountOrNull(data.original_payment_amount),
    refundedAmount: amountOrNull(data.refunded_amount),
    inProgressAmount: amountOrNull(data.in_progress_amount),
    history,
  }
}

function unavailable(message?: string): OrderRefundDetailsUI {
  return {
    kind: "details",
    status: RefundDetailsStatus.Unavailable,
    message: message || REFUND_DETAILS_FALLBACK_MESSAGE,
    currencyCode: null,
    originalPaymentAmount: null,
    refundedAmount: null,
    inProgressAmount: null,
    history: [],
  }
}
